import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

interface TwelvedataTimeSeriesValue {
  datetime: string;
  close: string;
  volume?: string;
}

interface TwelvedataErrorResponse {
  code?: number;
  message?: string;
  status?: 'error' | 'ok';
}

interface TwelvedataApiTestResult {
  valid: boolean;
  message: string;
  details?: {
    price?: string;
    symbol?: string;
    message?: string | undefined;
  };
}

/**
 * Twelvedata API服务
 * 备用API，支持股票、指数、外汇和加密货币
 * 免费方案：每分钟8次请求
 * 注意：可能需要API密钥，如果没有则使用demo模式
 */
class TwelvedataService {
  private client = axios.create({
    baseURL: 'https://api.twelvedata.com',
    timeout: 10000,
  });

  // API密钥（可以从环境变量获取，如果没有则使用demo模式）
  private apiKey: string | null = null;

  // Cache reference prices (e.g., yesterday close) to avoid calling /time_series on every refresh
  private referencePriceCache = new Map<string, { referencePrice: number; fetchedAt: number }>();
  private readonly REFERENCE_PRICE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

  constructor() {
    // 可以从环境变量或配置中获取API密钥
    // 免费方案：每分钟8次请求，无需API密钥（使用demo模式）
    // 付费方案：需要API密钥，提供更高的请求限制
    // 配置方式：在.env文件中添加 VITE_TWELVEDATA_API_KEY=your_api_key
    // 或者通过 settingsStore 设置
    // 优先级：用户配置（settingsStore）> 环境变量（env）
    
    // 优先级 1: 优先从 settingsStore（用户配置）读取
    try {
      const { useSettingsStore } = require('@/stores/settingsStore');
      const apiKeys = useSettingsStore.getState().apiKeys;
      if (apiKeys?.twelvedata && apiKeys.twelvedata.trim() !== '') {
        this.apiKey = apiKeys.twelvedata;
        return; // 如果找到用户配置，直接返回，不再检查环境变量
      }
    } catch (e) {
      // 忽略错误，继续检查环境变量
    }
    
    // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
    this.apiKey = (import.meta as any)?.env?.VITE_TWELVEDATA_API_KEY || null;

    // 添加响应拦截器，检查 JSON 响应中的 status 字段
    this.client.interceptors.response.use(
      (response) => {
        // 检查响应体中的 status 字段
        const data = response.data;
        if (data && typeof data === 'object' && data.status === 'error') {
          // 创建一个错误对象，模拟 HTTP 错误响应
          const error: any = new Error(data.message || 'API returned error status');
          error.response = {
            status: data.code || 400,
            data: {
              message: data.message || 'Unknown error',
              code: data.code,
              status: data.status,
            },
          };
          return Promise.reject(error);
        }
        return response;
      },
      (error) => {
        // 原有的错误处理
        return Promise.reject(error);
      }
    );
  }

  /**
   * 获取加密货币价格
   */
  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    try {
      const results: AssetPrice[] = [];
      let lastError: any = null;

      for (const id of ids) {
        try {
          // 转换ID为Twelvedata的符号格式（统一使用 SYMBOL/USD 格式）
          const baseSymbol = this.convertCryptoIdToSymbol(id);
          const symbol = `${baseSymbol}/USD`;
          
          console.log(`[TwelveData] 获取价格: ID="${id}" → Symbol="${symbol}"`);
          
          // 获取实时价格
          const priceResponse = await this.client.get('/price', {
            params: {
              symbol,
              apikey: this.apiKey || 'demo',
            },
          });

          if (priceResponse.data.price) {
            console.log(`[TwelveData] 成功获取 ${symbol} 价格: ${priceResponse.data.price}`);
            
            // 获取时间序列数据以计算24小时变化
            const timeSeriesResponse = await this.client.get('/time_series', {
              params: {
                symbol,
                interval: '1day',
                outputsize: 2,
                apikey: this.apiKey || 'demo',
              },
            }).catch(() => null);

            const currentPrice = parseFloat(priceResponse.data.price);
            let priceChange24h = 0;
            let priceChangePercent24h = 0;

            if (timeSeriesResponse?.data?.values) {
              const values = timeSeriesResponse.data.values;
              if (values.length >= 2) {
                const yesterdayPrice = parseFloat(values[1].close);
                priceChange24h = currentPrice - yesterdayPrice;
                priceChangePercent24h = (priceChange24h / yesterdayPrice) * 100;
              }
            }

            results.push({
              id: id.toLowerCase(),
              symbol: baseSymbol,
              name: this.getAssetName(id),
              current_price: currentPrice,
              price_change_24h: priceChange24h,
              price_change_percentage_24h: priceChangePercent24h,
              last_updated: new Date().toISOString(),
              source_url: this.client.defaults.baseURL || 'https://api.twelvedata.com',
            });
          }
        } catch (error) {
          console.error(`[TwelveData] 获取${id}价格失败:`, error);
          // 保存最后一个错误，如果所有请求都失败，则抛出
          lastError = error;
          // 继续处理其他资产
          continue;
        }
      }

      // 如果没有获取到任何价格，且有请求失败，抛出最后一个错误
      if (results.length === 0 && ids.length > 0 && lastError) {
        throw lastError;
      }

      return results;
    } catch (error) {
      console.error('[TwelveData] 获取加密货币价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取股票价格
   */
  async getStockPrice(symbol: string): Promise<AssetPrice> {
    try {
      const priceResponse = await this.client.get('/price', {
        params: {
          symbol,
          apikey: this.apiKey || 'demo',
        },
      });

      const currentPrice = parseFloat(priceResponse.data.price);
      let priceChange24h = 0;
      let priceChangePercent24h = 0;

      const cacheKey = `stock:${symbol.toUpperCase()}`;
      const referencePrice = await this.getOrFetchReferencePrice(cacheKey, symbol);
      if (referencePrice && referencePrice > 0) {
        priceChange24h = currentPrice - referencePrice;
        priceChangePercent24h = (priceChange24h / referencePrice) * 100;
      }

      return {
        id: symbol.toLowerCase(),
        symbol: symbol.toUpperCase(),
        name: symbol.toUpperCase(),
        current_price: currentPrice,
        price_change_24h: priceChange24h,
        price_change_percentage_24h: priceChangePercent24h,
        last_updated: new Date().toISOString(),
        source_url: this.client.defaults.baseURL || 'https://api.twelvedata.com',
      };
    } catch (error) {
      console.error('Twelvedata获取股票价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取指数价格
   */
  async getIndexPrice(symbol: string): Promise<AssetPrice> {
    try {
      // 指数符号可能需要特殊处理
      const indexSymbol = this.convertToTwelvedataSymbol(symbol, 'index');
      
      const priceResponse = await this.client.get('/price', {
        params: {
          symbol: indexSymbol,
          apikey: this.apiKey || 'demo',
        },
      });

      // Check if response contains error (TwelveData sometimes returns 200 with error in body)
      if (priceResponse.data && typeof priceResponse.data === 'object') {
        if ('code' in priceResponse.data && priceResponse.data.code !== 200) {
          const errorMsg = priceResponse.data.message || `Invalid symbol: ${indexSymbol}`;
          throw new Error(`Twelvedata API error: ${errorMsg}`);
        }
        if ('status' in priceResponse.data && priceResponse.data.status === 'error') {
          const errorMsg = priceResponse.data.message || `Invalid symbol: ${indexSymbol}`;
          throw new Error(`Twelvedata API error: ${errorMsg}`);
        }
      }

      // Check if price field exists
      if (!priceResponse.data || !('price' in priceResponse.data)) {
        throw new Error(`Invalid response format for symbol: ${indexSymbol}`);
      }

      const currentPrice = parseFloat(priceResponse.data.price);
      if (isNaN(currentPrice)) {
        throw new Error(`Invalid price value for symbol: ${indexSymbol}`);
      }

      let priceChange24h = 0;
      let priceChangePercent24h = 0;

      const cacheKey = `index:${indexSymbol.toUpperCase()}`;
      const referencePrice = await this.getOrFetchReferencePrice(cacheKey, indexSymbol);
      if (referencePrice && referencePrice > 0) {
        priceChange24h = currentPrice - referencePrice;
        priceChangePercent24h = (priceChange24h / referencePrice) * 100;
      }

      return {
        id: symbol.toLowerCase(),
        symbol: symbol.toUpperCase(),
        name: this.getIndexName(symbol),
        current_price: currentPrice,
        price_change_24h: priceChange24h,
        price_change_percentage_24h: priceChangePercent24h,
        last_updated: new Date().toISOString(),
        source_url: this.client.defaults.baseURL || 'https://api.twelvedata.com',
      };
    } catch (error: any) {
      // Enhanced error handling
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        if (data && typeof data === 'object' && 'message' in data) {
          throw new Error(`Twelvedata API error (${status}): ${data.message}`);
        }
        throw new Error(`Twelvedata API error (${status}): ${error.message || 'Unknown error'}`);
      }
      if (error.message) {
        throw error;
      }
      console.error('Twelvedata获取指数价格失败:', error);
      throw new Error(`Failed to get index price for ${symbol}: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * 获取外汇汇率
   */
  async getForexRate(from: string, to: string): Promise<AssetPrice> {
    try {
      // Twelvedata外汇格式：FROM/TO，例如USD/CNY
      const forexSymbol = `${from.toUpperCase()}/${to.toUpperCase()}`;
      
      const priceResponse = await this.client.get('/price', {
        params: {
          symbol: forexSymbol,
          apikey: this.apiKey || 'demo',
        },
      });

      const currentPrice = parseFloat(priceResponse.data.price);
      let priceChange24h = 0;
      let priceChangePercent24h = 0;

      const cacheKey = `forex:${forexSymbol.toUpperCase()}`;
      const referencePrice = await this.getOrFetchReferencePrice(cacheKey, forexSymbol);
      if (referencePrice && referencePrice > 0) {
        priceChange24h = currentPrice - referencePrice;
        priceChangePercent24h = (priceChange24h / referencePrice) * 100;
      }

      return {
        id: `${from.toLowerCase()}_${to.toLowerCase()}`,
        symbol: forexSymbol,
        name: forexSymbol, // Use symbol as name for consistency
        current_price: currentPrice,
        price_change_24h: priceChange24h,
        price_change_percentage_24h: priceChangePercent24h,
        last_updated: new Date().toISOString(),
        source_url: this.client.defaults.baseURL || 'https://api.twelvedata.com',
      };
    } catch (error) {
      console.error('Twelvedata获取外汇汇率失败:', error);
      throw error;
    }
  }

  /**
   * 获取历史数据
   */
  async getHistoricalData(id: string, days: number = 7): Promise<HistoricalData> {
    try {
      const baseSymbol = this.convertCryptoIdToSymbol(id);
      const symbol = `${baseSymbol}/USD`;
      const interval = days <= 1 ? '1h' : days <= 7 ? '4h' : '1day';
      const outputsize = days <= 1 ? 24 : days <= 7 ? 42 : days;

      const response = await this.client.get('/time_series', {
        params: {
          symbol,
          interval,
          outputsize,
          apikey: this.apiKey || 'demo',
        },
      });

      const values = response.data.values || [];
      
      const prices: [number, number][] = values
        .reverse() // 确保时间顺序正确
        .map((item: TwelvedataTimeSeriesValue) => [
          new Date(item.datetime).getTime(),
          parseFloat(item.close),
        ]);

      // 计算市场总值和交易量（使用价格作为近似值）
      const market_caps: [number, number][] = prices.map(([time, price]) => [time, price * 1000000]);
      const total_volumes: [number, number][] = values.map((item: TwelvedataTimeSeriesValue) => [
        new Date(item.datetime).getTime(),
        parseFloat(item.volume || '0'),
      ]);

      return {
        prices,
        market_caps,
        total_volumes,
      };
    } catch (error) {
      console.error('Twelvedata获取历史数据失败:', error);
      throw error;
    }
  }

  /**
   * 转换加密货币ID为交易符号
   * 智能处理多种ID格式，无需维护完整映射表
   */
  private convertCryptoIdToSymbol(id: string): string {
    // Handle "crypto-{symbol}" format (e.g., "crypto-doge" -> "DOGE")
    if (id.startsWith('crypto-')) {
      const symbol = id.substring(7); // Remove "crypto-" prefix
      return symbol.toUpperCase();
    }
    
    // Common cryptocurrency name to symbol mappings
    // Only include cases where the name is significantly different from the symbol
    const specialMappings: Record<string, string> = {
      'bitcoin': 'BTC',
      'ethereum': 'ETH',
      'binancecoin': 'BNB',
      'ripple': 'XRP',
      'dogecoin': 'DOGE',
      'polkadot': 'DOT',
      'polygon': 'MATIC',
      'avalanche': 'AVAX',
      'avalanche-2': 'AVAX',
      'chainlink': 'LINK',
      'uniswap': 'UNI',
      'litecoin': 'LTC',
      'solana': 'SOL',
      'cardano': 'ADA',
      'cosmos': 'ATOM',
      'tether': 'USDT',
    };
    
    const lowerCaseId = id.toLowerCase();
    
    // Check special mappings first
    if (specialMappings[lowerCaseId]) {
      return specialMappings[lowerCaseId];
    }
    
    // For most cryptocurrencies, the ID is either:
    // 1. Already the symbol (btc, eth, bnb) - just uppercase it
    // 2. The full name (solana, cardano, cosmos) - use first 3-4 letters uppercase
    // 3. Has a suffix like "coin" (e.g., "binancecoin" -> "BNB" handled above)
    
    // If ID is short (3-5 chars), it's likely already a symbol
    if (id.length >= 3 && id.length <= 5) {
      return id.toUpperCase();
    }
    
    // For longer names, try to extract a reasonable symbol
    // Remove common suffixes
    let cleanId = lowerCaseId
      .replace(/coin$/, '')
      .replace(/token$/, '')
      .replace(/-\d+$/, ''); // Remove version numbers like "-2"
    
    // If still long, take first 3-4 characters
    if (cleanId.length > 5) {
      return cleanId.substring(0, 4).toUpperCase();
    }
    
    return cleanId.toUpperCase();
  }

  /**
   * 转换ID为Twelvedata的符号格式
   */
  private convertToTwelvedataSymbol(id: string, type: 'crypto' | 'stock' | 'index' | 'forex'): string {
    const symbol = id.toUpperCase();
    
    // 加密货币可能需要添加交易所前缀或使用特定格式
    if (type === 'crypto') {
      // 使用新的转换方法
      return this.convertCryptoIdToSymbol(id);
    }
    
    // 指数符号映射：将常见名称转换为 Twelvedata 接受的符号
    if (type === 'index') {
      const indexSymbolMap: Record<string, string> = {
        'NASDAQ': 'IXIC',
        'nasdaq': 'IXIC',
        'NASDAQ Composite': 'IXIC',
        'nasdaq composite': 'IXIC',
        'SPX': 'SPX',
        'S&P500': 'SPX',
        'S&P 500': 'SPX',
        's&p500': 'SPX',
        's&p 500': 'SPX',
        'DJIA': 'DJI',
        'DOW': 'DJI',
        'Dow Jones': 'DJI',
        'dow jones': 'DJI',
      };
      
      const lowerSymbol = symbol.toLowerCase();
      const mappedSymbol = indexSymbolMap[symbol] || indexSymbolMap[lowerSymbol];
      if (mappedSymbol) {
        return mappedSymbol;
      }
    }
    
    return symbol;
  }

  /**
   * 获取资产名称
   * 智能处理多种ID格式
   */
  private getAssetName(id: string): string {
    // Handle "crypto-{symbol}" format (e.g., "crypto-btc" -> look up "btc")
    let lookupId = id;
    if (id.startsWith('crypto-')) {
      lookupId = id.substring(7); // Remove "crypto-" prefix
    }
    
    // Common cryptocurrency names
    const nameMap: Record<string, string> = {
      'btc': 'Bitcoin',
      'bitcoin': 'Bitcoin',
      'eth': 'Ethereum',
      'ethereum': 'Ethereum',
      'bnb': 'BNB',
      'binancecoin': 'BNB',
      'sol': 'Solana',
      'solana': 'Solana',
      'ada': 'Cardano',
      'cardano': 'Cardano',
      'xrp': 'XRP',
      'ripple': 'XRP',
      'doge': 'Dogecoin',
      'dogecoin': 'Dogecoin',
      'dot': 'Polkadot',
      'polkadot': 'Polkadot',
      'matic': 'Polygon',
      'polygon': 'Polygon',
      'avax': 'Avalanche',
      'avalanche': 'Avalanche',
      'avalanche-2': 'Avalanche',
      'link': 'Chainlink',
      'chainlink': 'Chainlink',
      'uni': 'Uniswap',
      'uniswap': 'Uniswap',
      'ltc': 'Litecoin',
      'litecoin': 'Litecoin',
      'atom': 'Cosmos',
      'cosmos': 'Cosmos',
    };
    
    const lowerCaseId = lookupId.toLowerCase();
    
    // Return mapped name if available
    if (nameMap[lowerCaseId]) {
      return nameMap[lowerCaseId];
    }
    
    // For unknown assets, capitalize the first letter of each word
    return lookupId
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * 获取指数名称
   */
  private getIndexName(symbol: string): string {
    const nameMap: Record<string, string> = {
      spx: 'S&P 500',
      's&p500': 'S&P 500',
      's&p 500': 'S&P 500',
      djia: 'Dow Jones',
      'dow jones': 'Dow Jones',
      nasdaq: 'NASDAQ',
      nasdaq100: 'NASDAQ 100',
      'nasdaq 100': 'NASDAQ 100',
    };
    return nameMap[symbol.toLowerCase()] || symbol.toUpperCase();
  }

  /**
   * 测试API密钥是否有效
   * @param apiKey 要测试的API密钥，如果为空则使用当前配置的密钥
   * @returns 测试结果对象，包含是否有效和详细信息
   */
  async testApiKey(apiKey?: string): Promise<TwelvedataApiTestResult> {
    const keyToTest = apiKey || this.apiKey || 'demo';
    
    try {
      // 使用一个简单的价格查询来测试API密钥
      // 使用BTC作为测试符号，因为它是最常见的
      const response = await this.client.get('/price', {
        params: {
          symbol: 'BTC',
          apikey: keyToTest,
        },
      });

      // 检查响应
      if (response.data.price) {
        // API密钥有效
        return {
          valid: true,
          message: keyToTest === 'demo' 
            ? '使用demo模式（无需API密钥）' 
            : 'API密钥有效',
          details: {
            price: response.data.price,
            symbol: 'BTC',
          },
        };
      } else if (response.data.message) {
        // API返回了错误消息
        return {
          valid: false,
          message: response.data.message || 'API密钥无效',
          details: {
            message: response.data.message,
          },
        };
      } else {
        // 响应格式异常
        return {
          valid: false,
          message: 'API响应格式异常',
          details: {
            message: 'API响应格式异常',
          },
        };
      }
    } catch (error: unknown) {
      // 处理错误
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { status: number; data: TwelvedataErrorResponse } };
        const status = axiosError.response.status;
        const data = axiosError.response.data;
        
        if (status === 401 || status === 403) {
          return {
            valid: false,
            message: 'API密钥无效或已过期',
            details: {
              message: data?.message,
            },
          };
        } else if (status === 429) {
          return {
            valid: false,
            message: '请求频率过高，请稍后再试',
            details: {
              message: data?.message,
            },
          };
        } else {
          return {
            valid: false,
            message: `API错误: ${data?.message || 'Unknown error'}`,
            details: {
              message: data?.message,
            },
          };
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return {
          valid: false,
          message: `网络错误: ${errorMessage}`,
        };
      }
    }
  }

  /**
   * 设置API密钥（用于运行时配置）
   */
  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  /**
   * 获取当前API密钥状态
   */
  getApiKeyStatus(): { hasKey: boolean; isDemo: boolean } {
    return {
      hasKey: this.apiKey !== null,
      isDemo: this.apiKey === null,
    };
  }

  private getCachedReferencePrice(cacheKey: string): number | null {
    const cached = this.referencePriceCache.get(cacheKey);
    if (!cached) return null;
    const age = Date.now() - cached.fetchedAt;
    if (age > this.REFERENCE_PRICE_TTL_MS) return null;
    return cached.referencePrice;
  }

  private async getOrFetchReferencePrice(cacheKey: string, symbol: string): Promise<number | null> {
    const cached = this.getCachedReferencePrice(cacheKey);
    if (cached !== null) return cached;

    // Fetch yesterday close once in a while; if it fails (e.g. rate limited), don't block price.
    const timeSeriesResponse = await this.client.get('/time_series', {
      params: {
        symbol,
        interval: '1day',
        outputsize: 2,
        apikey: this.apiKey || 'demo',
      },
    }).catch(() => null);

    if (timeSeriesResponse?.data?.values) {
      const values = timeSeriesResponse.data.values;
      if (values.length >= 2) {
        const yesterdayPrice = parseFloat(values[1].close);
        if (!isNaN(yesterdayPrice) && isFinite(yesterdayPrice) && yesterdayPrice > 0) {
          this.referencePriceCache.set(cacheKey, { referencePrice: yesterdayPrice, fetchedAt: Date.now() });
          return yesterdayPrice;
        }
      }
    }

    return null;
  }
}

export const twelvedataService = new TwelvedataService();

